import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheck, faCircle } from "@fortawesome/free-solid-svg-icons";
import { setRole, setCurrentOrgId, clearWorkspaceScopedSelections } from "../utils/permissions";
import "./AcceptInvite.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Universal accept-invite page.
//
// Two flows:
//   1. user_exists === true  -> the invited email already has an Ultimo
//      Bots account. The user must be logged in as that email; we then
//      send the request with their Bearer token and the backend just
//      adds an organization_members row.
//   2. user_exists === false -> the invited email is new. We collect
//      first/last/password and the backend creates the user and joins
//      them to the org in one transaction.
const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Form state for the new-user flow
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Same validation rules as ChangePasswordModal.
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push(t("password_min_8_chars"));
    if (!/[a-z]/.test(pwd)) errors.push(t("password_lowercase_required"));
    if (!/[A-Z]/.test(pwd)) errors.push(t("password_uppercase_required"));
    return errors;
  };

  // Fetch invitation preview on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}orgs/invitations/${encodeURIComponent(token)}`
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || t("invite_not_found"));
        }
        const data = await res.json();
        if (alive) setInvitation(data);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, t]);

  // Accept handler -- works for both flows. The body is ignored by the
  // backend when an existing user accepts via Bearer token.
  const acceptInvitation = useCallback(
    async (body, useBearer) => {
      const headers = { "Content-Type": "application/json" };
      if (useBearer) {
        const bearer = localStorage.getItem("token");
        if (!bearer) throw new Error(t("invite_must_be_logged_in"));
        headers["Authorization"] = `Bearer ${bearer}`;
      }
      const res = await fetch(
        `${API_BASE}orgs/invitations/${encodeURIComponent(token)}/accept`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(body || {}),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || t("invite_accept_failed"));
      }
      return res.json();
    },
    [token, t]
  );

  const persistSessionAndGo = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("user_id", data.user_id);
    if (data.first_name) localStorage.setItem("first_name", data.first_name);
    if (data.language) localStorage.setItem("language", data.language);
    if (data.date_format) localStorage.setItem("date_format", data.date_format);
    localStorage.setItem("timezone", data.timezone || "UTC");
    setRole(data.role, data.owner_user_id);
    if (data.current_org_id !== undefined && data.current_org_id !== null) {
      setCurrentOrgId(data.current_org_id);
    }
    // We're landing in a freshly-joined workspace; any prior selectedBot*
    // entries belong to the previous one and would 403.
    clearWorkspaceScopedSelections();
    // Hard reload so all auth-dependent components re-mount with the
    // newly issued token + org context.
    window.location.href = "/my-bots";
  };

  // New-user submit
  const handleCreateAndAccept = async (e) => {
    e?.preventDefault?.();
    setError(null);

    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setError(pwdErrors.join(", "));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("passwords_do_not_match"));
      return;
    }

    setSubmitting(true);
    try {
      const data = await acceptInvitation(
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          password,
        },
        false
      );
      persistSessionAndGo(data);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Existing-user accept (must be logged in)
  const handleJoinAsExistingUser = async () => {
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const bearer = localStorage.getItem("token");
      if (!bearer) {
        // Send them to login first; bring them back here on success.
        const returnTo = encodeURIComponent(
          `/accept-invite/${encodeURIComponent(token)}`
        );
        navigate(`/login?returnTo=${returnTo}`);
        return;
      }
      // The backend may reject if the logged-in user's email differs
      // from the invited email (403). Let it surface.
      const data = await acceptInvitation({}, true);
      // If we got here, the membership row is in place. Switch the
      // active org to the inviting workspace and refresh the SPA.
      const switchRes = await fetch(`${API_BASE}orgs/switch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token || bearer}`,
        },
        body: JSON.stringify({ organization_id: data.current_org_id }),
      });
      if (switchRes.ok) {
        const sw = await switchRes.json();
        localStorage.setItem("token", sw.token);
        setRole(sw.role, sw.owner_user_id);
        setCurrentOrgId(sw.current_org_id);
      } else if (data.token) {
        // Fall back to the token returned from accept.
        persistSessionAndGo(data);
        return;
      }
      setInfo(t("invite_joined_redirecting"));
      clearWorkspaceScopedSelections();
      window.location.href = "/my-bots";
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <p>{t("invite_loading")}</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <h1>{t("invite_unavailable_title")}</h1>
          <p className="accept-subtitle">{error}</p>
          <a className="accept-link" href="/login">
            {t("invite_go_to_login")}
          </a>
        </div>
      </div>
    );
  }

  const orgName = invitation.organization_name || t("invite_default_workspace_name");
  const loggedInUserId = localStorage.getItem("user_id");
  const isLoggedIn = Boolean(localStorage.getItem("token"));

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-card">
        <img
          src="/ultimo-bots-purple.png"
          alt="Ultimo Bots"
          className="accept-invite-logo"
        />
        <h1>{t("invite_join_heading", { orgName })}</h1>
        <p className="accept-subtitle">
          {t("invite_subtitle")}
        </p>

        <div className="invite-summary">
          <div>
            <Trans
              i18nKey="invite_invited_by"
              values={{ name: invitation.inviter_name }}
              components={{ strong: <strong /> }}
            >
              {"Invited by <strong>{{name}}</strong>"}
            </Trans>
            {invitation.inviter_email ? (
              <span> ({invitation.inviter_email})</span>
            ) : null}
          </div>
          <div style={{ marginTop: 6 }}>
            {t("invite_workspace_label")} <strong>{orgName}</strong>
          </div>
          <div style={{ marginTop: 6 }}>
            {t("invite_email_label")} <strong>{invitation.email}</strong>
          </div>
          <div style={{ marginTop: 6 }}>
            {t("invite_access_level_label")} <strong>{invitation.role}</strong>
          </div>
        </div>

        {invitation.user_exists ? (
          // Flow 1: existing Ultimo Bots user. They need to be logged
          // in as that account, then we just add them to the org.
          <div>
            <p className="accept-subtitle" style={{ marginTop: 16 }}>
              <Trans
                i18nKey="invite_existing_account"
                values={{ email: invitation.email }}
                components={{ strong: <strong /> }}
              >
                {"An Ultimo Bots account already exists for <strong>{{email}}</strong>. Log in to that account to add this workspace to your account. You'll be able to switch between workspaces from the Workspaces page."}
              </Trans>
            </p>
            <button
              className="accept-button"
              type="button"
              onClick={handleJoinAsExistingUser}
              disabled={submitting}
            >
              {submitting
                ? t("invite_joining")
                : isLoggedIn
                ? t("invite_join_as_logged_in", { orgName })
                : t("invite_login_to_accept")}
            </button>
            {isLoggedIn && loggedInUserId && (
              <p className="accept-subtitle" style={{ marginTop: 8, fontSize: 12 }}>
                {t("invite_logged_in_user_hint", { loggedInUserId })}
              </p>
            )}
          </div>
        ) : (
          // Flow 2: brand-new user. Collect name + password and create
          // the account + join the org in one shot.
          <form onSubmit={handleCreateAndAccept} autoComplete="off">
            {/* Hidden username field so the browser associates the
                password fields with the invited email and doesn't try
                to autofill the last-name input with the email. */}
            <input
              type="email"
              name="username"
              value={invitation.email || ""}
              autoComplete="username"
              readOnly
              hidden
              tabIndex={-1}
            />
            <TextField
              label={t("first_name") || "First name"}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
              margin="normal"
              autoComplete="given-name"
              name="given-name"
            />
            <TextField
              label={t("last_name") || "Last name"}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              margin="normal"
              autoComplete="family-name"
              name="family-name"
            />
            <TextField
              label={t("password") || "Password"}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              margin="normal"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={t("aria_toggle_password_visibility")}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t("confirm_password") || "Confirm Password"}
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              margin="normal"
              autoComplete="new-password"
              error={confirmPassword.length > 0 && confirmPassword !== password}
              helperText={
                confirmPassword.length > 0 && confirmPassword !== password
                  ? t("passwords_do_not_match")
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={t("aria_toggle_password_visibility")}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEyeSlash : faEye}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div
              style={{
                marginTop: 2,
                marginBottom: 4,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              {[
                { ok: password.length >= 8, label: t("password_min_8_chars") },
                { ok: /[a-z]/.test(password), label: t("password_lowercase_required") },
                { ok: /[A-Z]/.test(password), label: t("password_uppercase_required") },
              ].map((rule, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: rule.ok ? "#5616ea" : "#6b7280",
                  }}
                >
                  <FontAwesomeIcon
                    icon={rule.ok ? faCheck : faCircle}
                    style={{
                      fontSize: rule.ok ? 12 : 6,
                      width: 12,
                      color: rule.ok ? "#5616ea" : "#9ca3af",
                    }}
                  />
                  <span>{rule.label}</span>
                </div>
              ))}
            </div>
            <button
              className="accept-button"
              type="submit"
              disabled={submitting}
            >
              {submitting ? t("invite_creating_account") : t("invite_accept_create_account")}
            </button>
          </form>
        )}

        {error && <div className="accept-error">{error}</div>}
        {info && <div className="accept-success">{info}</div>}
      </div>
    </div>
  );
};

export default AcceptInvite;
